import { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTheme } from "@/components/ui/theme-provider";
import { DocumentViewer } from "@/components/document-viewer";
import { MainContentWrapper } from "@/components/main-content-wrapper";
import { DocumentAuthModal } from "@/components/document-auth-modal";
import { DocumentUsersDialog } from "@/components/document-users-dialog";
import { AdminOnly } from "@/components/admin-only";
import { ArrowLeft, Sun, Moon, Users, Check, X, Link, MoreVertical, UserCheck, Eye } from "lucide-react";
import { ReadReceiptsPanel } from "@/components/read-receipts-popover";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useDocumentAuth } from "@/hooks/use-document-auth";
import { useAuth } from "@/hooks/useAuth";
import type { Document, DocumentVersion } from "@shared/schema";

// Editable Document Name Component
function EditableDocumentName({ document }: { document: Document }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(document.name);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const updateDocumentMutation = useMutation({
    mutationFn: async (newName: string) => {
      return apiRequest("PATCH", `/api/documents/${document.id}`, { name: newName });
    },
    onMutate: async (newName: string) => {
      await queryClient.cancelQueries({ queryKey: ["/api/documents"] });
      await queryClient.cancelQueries({ queryKey: ["/api/documents", document.id] });

      const previousDocuments = queryClient.getQueryData(["/api/documents"]);
      const previousDocument = queryClient.getQueryData(["/api/documents", document.id]);

      queryClient.setQueryData(["/api/documents"], (old: Document[] | undefined) => {
        if (!old) return old;
        return old.map(doc =>
          doc.id === document.id ? { ...doc, name: newName } : doc
        );
      });

      queryClient.setQueryData(["/api/documents", document.id], (old: any) => {
        if (!old) return old;
        return { ...old, name: newName };
      });

      return { previousDocuments, previousDocument };
    },
    onError: (err, newName, context) => {
      if (context?.previousDocuments) {
        queryClient.setQueryData(["/api/documents"], context.previousDocuments);
      }
      if (context?.previousDocument) {
        queryClient.setQueryData(["/api/documents", document.id], context.previousDocument);
      }

      toast({
        title: "Error",
        description: "Failed to update document name",
        variant: "destructive",
      });

      setEditValue(document.name);
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Document name updated",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/documents", document.id] });
    },
  });

  const handleSave = () => {
    if (editValue.trim() !== document.name && editValue.trim() !== "") {
      updateDocumentMutation.mutate(editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(document.name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          autoFocus
          className="text-lg font-semibold bg-transparent border-0 p-0 h-auto focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSave}
            disabled={updateDocumentMutation.isPending}
            className="h-6 w-6 p-0 flex-shrink-0"
          >
            <Check className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            disabled={updateDocumentMutation.isPending}
            className="h-6 w-6 p-0 flex-shrink-0"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <h1
      className="text-lg font-semibold text-foreground truncate cursor-pointer transition-colors"
      style={{
        '--hover-color': document.defaultIndicatorColor
      } as React.CSSProperties}
      onClick={() => setIsEditing(true)}
      title="Click to edit"
      onMouseEnter={(e) => {
        e.currentTarget.style.color = document.defaultIndicatorColor;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = '';
      }}
    >
      {document.name}
      {updateDocumentMutation.isPending && (
        <span className="ml-2 text-xs text-muted-foreground">Saving...</span>
      )}
    </h1>
  );
}

// Shared minimal header used across all loading/error states
function MinimalHeader({ onToggleTheme, theme }: { onToggleTheme: () => void; theme: string }) {
  return (
    <header className="bg-background border-b border-border sticky top-0 z-50">
      <div className="px-3 sm:px-4">
        <div className="flex justify-between items-center h-12">
          <div className="flex items-center gap-2">
            <div className="h-4 bg-muted rounded w-32 animate-pulse" />
          </div>
          <Button variant="ghost" size="sm" onClick={onToggleTheme} className="h-8 w-8 p-0">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </header>
  );
}

export default function DocumentView() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { toast } = useToast();

  const documentId = params.documentId || null;

  // Parse mode and sub-mode from current location
  const location = window.location.pathname;
  const mode = location.includes('/comment/') ? 'comment' :
               location.includes('/browse/') ? 'browse' :
               location.includes('/compare/') ? 'compare' : 'comment';

  const compareSubMode = location.includes('/diff-heatmap/') ? 'diff-heatmap' : 'side-by-side';

  // Document authentication — always called unconditionally so hook order is stable
  const auth = useDocumentAuth({
    documentId: documentId || ''
  });

  const {
    currentUser,
    isModalOpen,
    isInitialized,
    openModal,
    closeModal,
    users,
    isLoadingUsers,
    login,
    createUser,
    clearIdentity,
    showOnboarding,
    dismissOnboarding
  } = auth;

  // Document users dialog state
  const [isUsersDialogOpen, setIsUsersDialogOpen] = useState(false);

  // Mobile menu + read receipts panel state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [readReceiptsOpen, setReadReceiptsOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Close mobile menu on outside click
  useEffect(() => {
    if (!mobileMenuOpen) return;
    function handlePointerDown(e: PointerEvent) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setMobileMenuOpen(false);
    }
    const doc = window.document;
    const timer = setTimeout(() => {
      doc.addEventListener("pointerdown", handlePointerDown);
      doc.addEventListener("keydown", handleKeyDown);
    }, 0);
    return () => {
      clearTimeout(timer);
      doc.removeEventListener("pointerdown", handlePointerDown);
      doc.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileMenuOpen]);

  const handleCopyLink = async () => {
    if (!documentId) return;
    const baseUrl = `/doc/${documentId}`;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${baseUrl}`);
      toast({ title: "Link copied", description: "Document link copied to clipboard." });
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Parse version and page parameters based on mode
  const versionNumber = mode === 'compare'
    ? (params.versionIdA ? parseInt(params.versionIdA) : null)
    : (params.versionId ? parseInt(params.versionId) : null);
  const versionNumberB = mode === 'compare' && params.versionIdB ? parseInt(params.versionIdB) : null;
  const pageNumber = params.pageNumber ? parseInt(params.pageNumber) : 1;
  const indicatorNumber = params.indicatorNumber ? parseInt(params.indicatorNumber) : null;

  const { user: authUser, isLoading: isLoadingAuth } = useAuth();

  const [showJoinedBadge, setShowJoinedBadge] = useState(false);
  const lastRegisteredDocId = useRef<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!authUser || !documentId || lastRegisteredDocId.current === documentId) return;
    lastRegisteredDocId.current = documentId;

    (async () => {
      try {
        const res = await apiRequest('POST', `/api/documents/${documentId}/self-register`);
        const data = await res.json();
        if (data.user) {
          login(data.user);
        }
        if (data.created) {
          setShowJoinedBadge(true);
          queryClient.invalidateQueries({ queryKey: ['/api/documents', documentId, 'users'] });
          setTimeout(() => setShowJoinedBadge(false), 3000);
        }
      } catch {
        // Silent failure — don't disrupt the viewing experience
      }
    })();
  }, [authUser, documentId, queryClient, login]);

  const { data: documents = [], isLoading: isLoadingDocuments } = useQuery<Document[]>({
    queryKey: ["/api/documents"],
    enabled: !!authUser,
  });

  // Fallback query for public document access (guests or when not in the documents list)
  const { data: directDocument, isLoading: isLoadingDirectDocument } = useQuery<Document>({
    queryKey: ["/api/documents", documentId],
    enabled: !!documentId && !isLoadingAuth && (documents.length === 0 || !documents.find(d => d.id === documentId)),
  });

  const document = documentId
    ? (documents.find(doc => doc.id === documentId) || directDocument)
    : null;

  // Fetch document with versions — distinct query key to avoid cache collision
  const { data: documentWithVersions, isLoading: isLoadingDocumentVersions } = useQuery({
    queryKey: ["/api/documents", documentId, "with-versions"],
    queryFn: async () => {
      const res = await fetch(`/api/documents/${documentId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch document");
      return res.json();
    },
    enabled: !!documentId && !!document,
  });

  const versions = (documentWithVersions as any)?.versions || [];
  const latestVersion = versions[0];

  // Resolve the active version ID for read-receipts (mirrors what DocumentViewer uses)
  const activeVersionObj = versionNumber
    ? versions.find((v: any) => v.version === versionNumber)
    : latestVersion;
  const activeVersionId: string | undefined = activeVersionObj?.id;
  const activeVersionNumber: number | undefined = activeVersionObj?.version;

  // Redirect to full URL format if accessing base document URL
  useEffect(() => {
    if (document && latestVersion && !versionNumber && !params.pageNumber && mode === 'comment') {
      setLocation(`/doc/${documentId}/comment/v/${latestVersion.version}/p/1`);
    }
  }, [document, latestVersion, versionNumber, params.pageNumber, documentId, setLocation, mode]);

  // Prevent search engine indexing of document pages
  useEffect(() => {
    const robotsMetaTag = window.document.createElement('meta');
    robotsMetaTag.name = 'robots';
    robotsMetaTag.content = 'noindex, nofollow';
    robotsMetaTag.id = 'document-robots-meta';
    window.document.head.appendChild(robotsMetaTag);
    return () => {
      const existingTag = window.document.getElementById('document-robots-meta');
      if (existingTag) {
        window.document.head.removeChild(existingTag);
      }
    };
  }, []);

  // The modal should only appear after the localStorage check has run (isInitialized),
  // and only for guests (not org members who are already fully authenticated).
  const guestModalOpen = !authUser && isInitialized && isModalOpen;

  const isLoading = isLoadingAuth || isLoadingDocuments || isLoadingDocumentVersions || isLoadingDirectDocument;

  // ── Loading state ──────────────────────────────────────────────────────────
  // Modal is rendered here too so guests see it immediately while document loads.
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <DocumentAuthModal
          isOpen={guestModalOpen}
          onClose={closeModal}
          users={users}
          isLoadingUsers={isLoadingUsers}
          onLogin={login}
          onCreateUser={createUser}
          documentColor="#6366f1"
          documentOrganizationId={undefined}
        />
        <MinimalHeader onToggleTheme={toggleTheme} theme={theme} />
        <div className="p-3">
          <div className="animate-pulse space-y-3">
            <div className="h-6 bg-muted rounded w-1/3" />
            <div className="h-80 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  // ── Not found ──────────────────────────────────────────────────────────────
  // Modal is rendered here too in case the document lookup failed but the guest
  // hasn't identified themselves yet.
  if (!document) {
    return (
      <div className="min-h-screen bg-background">
        <DocumentAuthModal
          isOpen={guestModalOpen}
          onClose={closeModal}
          users={users}
          isLoadingUsers={isLoadingUsers}
          onLogin={login}
          onCreateUser={createUser}
          documentColor="#6366f1"
          documentOrganizationId={undefined}
        />
        <header className="bg-background border-b border-border sticky top-0 z-50">
          <div className="px-3 sm:px-4">
            <div className="flex justify-between items-center h-12">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-foreground">Not Found</h1>
              </div>
              <Button variant="ghost" size="sm" onClick={toggleTheme} className="h-8 w-8 p-0">
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </header>
        <div className="p-6 text-center">
          <p className="text-muted-foreground mb-4">Document not found</p>
          <Button size="sm" onClick={() => setLocation("/")}>Go back</Button>
        </div>
      </div>
    );
  }

  // ── Full document view ─────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      {/* Authentication Modal — only for guests, always mounted so it can open at any time */}
      {!authUser && (
        <DocumentAuthModal
          isOpen={isModalOpen}
          onClose={closeModal}
          users={users}
          isLoadingUsers={isLoadingUsers}
          onLogin={login}
          onCreateUser={createUser}
          documentColor={document.defaultIndicatorColor}
          documentOrganizationId={document.organizationId}
        />
      )}

      {/* Document Users Dialog */}
      <DocumentUsersDialog
        isOpen={isUsersDialogOpen}
        onClose={() => setIsUsersDialogOpen(false)}
        users={users}
        isLoading={isLoadingUsers}
        documentName={document.name}
        documentId={document.id}
        baseUrl={window.location.origin}
        document={document}
        currentUser={currentUser}
        latestVersion={latestVersion}
      />

      {/* Compact Header */}
      <header className="bg-background border-b border-border sticky top-0 z-50 -mb-3">
        <div className="px-3 sm:px-4">
          <div className="flex justify-between items-center h-12">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <AdminOnly>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLocation("/")}
                  className="h-8 w-8 p-0 flex-shrink-0"
                  title="Back to dashboard"
                  aria-label="Back to dashboard"
                  data-testid="button-back-dashboard"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </AdminOnly>
              <EditableDocumentName document={document} />
            </div>

            <div className="flex items-center gap-2">
              {showJoinedBadge && (
                <div
                  className="flex items-center gap-1 bg-green-500/10 text-green-600 dark:text-green-400 px-2 py-1 rounded text-xs whitespace-nowrap animate-in fade-in slide-in-from-right-2 duration-300 transition-opacity"
                  style={{ animation: 'fadeInOut 3s ease-in-out forwards' }}
                >
                  <UserCheck className="h-3 w-3" />
                  <span>Joined</span>
                </div>
              )}

              {/* More Options Menu — visible on all screen sizes */}
              <div ref={mobileMenuRef} className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 flex-shrink-0"
                  title="More options"
                  data-testid="button-mobile-menu"
                  onClick={() => setMobileMenuOpen((prev) => !prev)}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>

                {mobileMenuOpen && (
                  <div className="absolute top-full right-0 mt-1 z-50 min-w-[180px] rounded-md border border-border bg-popover p-1 shadow-md">
                    {currentUser && !authUser && (
                      <button
                        className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                        data-testid="menu-item-switch-identity"
                        onClick={() => { setMobileMenuOpen(false); clearIdentity(); }}
                      >
                        <Users className="h-4 w-4 mr-2 flex-shrink-0" />
                        Identity ({currentUser.name.split(' ')[0]})
                      </button>
                    )}
                    <button
                      className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                      data-testid="menu-item-view-users"
                      onClick={() => { setMobileMenuOpen(false); setIsUsersDialogOpen(true); }}
                    >
                      <Users className="h-4 w-4 mr-2 flex-shrink-0" />
                      View Users
                    </button>
                    <button
                      className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                      data-testid="menu-item-read-receipts"
                      onClick={() => { setMobileMenuOpen(false); setReadReceiptsOpen(true); }}
                    >
                      <Eye className="h-4 w-4 mr-2 flex-shrink-0" />
                      Read Receipts
                    </button>
                    <button
                      className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                      data-testid="menu-item-copy-link"
                      onClick={() => { setMobileMenuOpen(false); handleCopyLink(); }}
                    >
                      <Link className="h-4 w-4 mr-2 flex-shrink-0" />
                      Copy Link
                    </button>
                    <button
                      className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                      data-testid="menu-item-toggle-theme"
                      onClick={() => { setMobileMenuOpen(false); toggleTheme(); }}
                    >
                      {theme === "dark" ? (
                        <>
                          <Sun className="h-4 w-4 mr-2 flex-shrink-0" />
                          Light Mode
                        </>
                      ) : (
                        <>
                          <Moon className="h-4 w-4 mr-2 flex-shrink-0" />
                          Dark Mode
                        </>
                      )}
                    </button>
                    {currentUser && (
                      <>
                        <div className="my-1 border-t border-border" />
                        <div className="px-2 py-1.5 text-xs text-muted-foreground truncate">
                          Commenting as <span className="font-medium text-foreground">{currentUser.name}</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Read Receipts Panel — triggered from mobile menu */}
              {document && (
                <ReadReceiptsPanel
                  open={readReceiptsOpen}
                  onClose={() => setReadReceiptsOpen(false)}
                  documentId={document.id}
                  versionId={activeVersionId}
                  versionNumber={activeVersionNumber}
                  documentColor={document.defaultIndicatorColor}
                />
              )}

            </div>
          </div>
        </div>
      </header>

      {/* Document Viewer */}
      <MainContentWrapper>
        <DocumentViewer
          document={document}
          initialVersionId={versionNumber ? versions.find((v: any) => v.version === versionNumber)?.id : null}
          initialVersionIdB={mode === 'compare' && versionNumberB ? versions.find((v: any) => v.version === versionNumberB)?.id : null}
          initialPageNumber={pageNumber}
          initialIndicatorNumber={indicatorNumber}
          initialMode={mode as "comment" | "browse" | "compare"}
          compareSubMode={compareSubMode as "side-by-side" | "diff-heatmap"}
          currentUser={currentUser}
          documentUsers={users}
          onboardingState={currentUser ? { showOnboarding, dismissOnboarding } : undefined}
        />
      </MainContentWrapper>
    </div>
  );
}
